import { Phone, Mail, Clock, MapPin } from "lucide-react"
import Bottom from "./Footer/BottomBar"
import { useCompanyInfo } from "@/shared/hooks/useCompanyInfo"

export default function Footer() {
  const { getByKey } = useCompanyInfo()

  // Fallback values while loading or if data not available
  const companyName = getByKey('company_name') || 'Travel Warehouse Inc.'
  const companyTagline = getByKey('company_tagline') || 'Your trusted partner for exceptional travel experiences since 1999'
  const companyDescription = getByKey('company_description') || 'Creating unforgettable memories around the world.'
  const email = getByKey('email_primary') || 'info@twi.com.ph'
  const phoneMobile = getByKey('phone_mobile') || '(+63) 985 590 9000'
  const phoneLandline1 = getByKey('phone_landline_1') || '(02) 8687-2490'
  const phoneLandline2 = getByKey('phone_landline_2') || '(02) 8687-2491'
  const address = getByKey('address_full') || '1007 OMM Citra Bldg, San Miguel Ave., Ortigas Center, Pasig City Philippines 1600'
  const businessHours = getByKey('business_hours_weekday') || 'Monday - Friday: 8:30 AM - 5:30 PM'
  const facebookUrl = getByKey('social_facebook') || 'https://www.facebook.com/travelwarehouseinc'
  const instagramUrl = getByKey('social_instagram') || 'https://www.instagram.com/twirista/'
  const youtubeUrl = getByKey('social_youtube') || 'https://www.youtube.com/@TWIrista'
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main footer content */}
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-6 lg:gap-8">
            {/* Company Info */}
            <div className="sm:col-span-2 lg:col-span-1 animate-in fade-in duration-700">
              <div className="flex items-center mb-4 sm:mb-5">
                <div>
                  <h3 className="text-2xl font-bold">
                    {companyName.replace(' Inc.', '')} <span className="text-amber-600">Inc.</span>
                  </h3>
                  <div className="h-1 w-16 bg-amber-500 mt-2 rounded-full"></div>
                </div>
              </div>
              <p className="text-gray-300 mb-6 text-sm sm:text-base leading-relaxed">
                {companyTagline}. {companyDescription}
              </p>
              <div className="flex items-start gap-3 text-gray-300 mb-6 p-3 sm:p-4 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300 rounded-lg">
                <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open address in Google Maps: ${address}`}
                  className="text-sm leading-relaxed text-white hover:text-amber-400 underline-offset-2 hover:underline"
                >
                  {address}
                </a>
              </div>
            </div>

            {/* Get In Touch */}
            <div className="animate-in fade-in duration-700" style={{ animationDelay: '200ms' }}>
              <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-5 text-white flex items-center gap-2">
                <span className="w-1 h-6 bg-amber-500 rounded-full"></span>
                Get In Touch
              </h4>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 backdrop-blur-sm transition-all duration-300 group rounded-lg">
                  <Mail className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Email Us</p>
                    <a href={`mailto:${email}`} className="text-white hover:text-amber-400 transition-colors duration-300 font-medium">
                      {email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 backdrop-blur-sm transition-all duration-300 group rounded-lg">
                  <Phone className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Call Us</p>
                    <p className="text-white font-medium">{phoneMobile}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 backdrop-blur-sm transition-all duration-300 group rounded-lg">
                  <Phone className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Hotline</p>
                    <p className="text-white font-medium">{phoneLandline1} / {phoneLandline2.split(' ').pop()}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 backdrop-blur-sm transition-all duration-300 group rounded-lg">
                  <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Business Hours</p>
                    <p className="text-white font-medium">{businessHours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Follow Us */}
            <div className="animate-in fade-in duration-700" style={{ animationDelay: '400ms' }}>
              <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-5 text-white flex items-center gap-2">
                <span className="w-1 h-6 bg-orange-400 rounded-full"></span>
                Follow Us
              </h4>
              <div className="flex flex-col space-y-4">
                {/* Facebook */}
                {facebookUrl && (
                  <a
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="flex items-center justify-center w-16 h-16">
                      <img src="/FB.png" alt="Facebook" className="w-14 h-14 group-hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.9)]" loading="lazy" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-lg">Facebook</p>
                      <p className="text-gray-400 text-sm group-hover:text-gray-300">@travelwarehouseinc</p>
                    </div>
                  </a>
                )}

                {/* Instagram */}
                {instagramUrl && (
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="flex items-center justify-center w-16 h-16">
                      <img src="/INSTA.png" alt="Instagram" className="w-14 h-14 group-hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.9)]" loading="lazy" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-lg">Instagram</p>
                      <p className="text-gray-400 text-sm group-hover:text-gray-300">@twirista</p>
                    </div>
                  </a>
                )}

                {/* YouTube */}
                {youtubeUrl && (
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="flex items-center justify-center w-16 h-16">
                      <img src="/YT.png" alt="YouTube" className="w-14 h-14 group-hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.9)]" loading="lazy" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-lg">YouTube</p>
                      <p className="text-gray-400 text-sm group-hover:text-gray-300">@TWIrista</p>
                    </div>
                  </a>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Bottom bar */}
        <Bottom />
      </div>
    </footer>
  )
}
